/**
 * Campaign Generation Demo Component
 * Demonstrates AI-driven campaign generation from A/B test winners
 */

import React, { useState } from 'react';
import {
  CampaignGenerationService,
  DEFAULT_CAMPAIGN_GENERATION_CONFIG,
  GeneratedCampaign
} from '../../../services/campaignGenerationService';
import { WinnerDetectionEngine, WINNER_DETECTION_PRESETS } from '../../../services/winnerDetectionEngine';
import { Template, ABTest, ABTestVariant } from '../../../types/template';
import CampaignGenerationDashboard from './CampaignGenerationDashboard';

export const CampaignGenerationDemo: React.FC = () => {
  const [campaigns, setCampaigns] = useState<GeneratedCampaign[]>([]);
  const [_selectedCampaign, setSelectedCampaign] = useState<GeneratedCampaign | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationLog, setGenerationLog] = useState<string[]>([]);

  const campaignService = new CampaignGenerationService(DEFAULT_CAMPAIGN_GENERATION_CONFIG);
  const winnerDetectionEngine = new WinnerDetectionEngine(WINNER_DETECTION_PRESETS.moderate);

  // Demo data
  const createDemoTemplate = (): Template => ({
    id: 'demo-template-1',
    name: 'Summer Sale Newsletter',
    category: 'promotional',
    thumbnail: 'demo-thumb.jpg',
    content: [
      {
        id: 'header-block',
        type: 'text',
        content: { text: 'Summer Sale - Up to 50% Off!' },
        styles: {},
        responsive: { desktop: {}, tablet: {}, mobile: {} },
        position: { order: 1 },
        metadata: { locked: false, hidden: false }
      },
      {
        id: 'cta-block',
        type: 'button',
        content: { text: 'Shop Now', url: 'https://example.com/shop' },
        styles: {},
        responsive: { desktop: {}, tablet: {}, mobile: {} },
        position: { order: 2 },
        metadata: { locked: false, hidden: false }
      }
    ],
    metadata: {
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'demo-user',
      version: 1,
      tags: ['summer', 'sale', 'promotional'],
      isPublic: false
    },
    collaboration: {
      currentEditors: [],
      permissions: [],
      comments: []
    }
  });

  const createDemoABTest = (template: Template): ABTest => {
    const controlVariant: ABTestVariant = {
      id: 'control-variant',
      name: 'Control',
      isControl: true,
      trafficAllocation: 50,
      template,
      changes: []
    };

    const testVariant: ABTestVariant = {
      id: 'test-variant',
      name: 'Personalized Version',
      isControl: false,
      trafficAllocation: 50,
      template,
      changes: [
        {
          blockId: 'header-block',
          changeType: 'content',
          description: 'Added personalization',
          oldValue: { text: 'Summer Sale - Up to 50% Off!' },
          newValue: { text: 'Hey {{firstName}}, Summer Sale - Up to 50% Off!' },
          confidence: 85
        },
        {
          blockId: 'cta-block',
          changeType: 'content',
          description: 'More urgent CTA',
          oldValue: { text: 'Shop Now' },
          newValue: { text: 'Shop Now - Limited Time!' },
          confidence: 78
        }
      ]
    };

    return {
      id: `demo-test-${Date.now()}`,
      name: 'Summer Sale Personalization Test',
      templateId: template.id,
      status: 'completed',
      createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // 14 days ago
      completedAt: new Date(),
      variants: [controlVariant, testVariant],
      testableElements: [
        {
          blockId: 'header-block',
          blockType: 'text',
          elementName: 'Header Text',
          testType: 'content',
          suggestions: [],
          priority: 'high',
          isMarked: true
        },
        {
          blockId: 'cta-block',
          blockType: 'button',
          elementName: 'CTA Button',
          testType: 'content',
          suggestions: [],
          priority: 'high',
          isMarked: true
        }
      ],
      configuration: {
        trafficSplit: [50, 50],
        successMetrics: [
          {
            id: 'open-rate',
            name: 'Open Rate',
            type: 'open_rate',
            isPrimary: true,
            weight: 1
          }
        ],
        duration: { type: 'fixed', days: 14 },
        statisticalSettings: {
          confidenceLevel: 95,
          minimumDetectableEffect: 5,
          powerAnalysis: 80
        }
      },
      results: {
        status: 'completed',
        winner: 'test-variant',
        confidence: 96.5,
        statisticalSignificance: true,
        variants: [
          {
            variantId: 'control-variant',
            metrics: {
              'open-rate': {
                value: 22.3,
                improvement: 0,
                confidence: 95,
                isSignificant: false
              }
            },
            sampleSize: 5000,
            conversionRate: 22.3
          },
          {
            variantId: 'test-variant',
            metrics: {
              'open-rate': {
                value: 28.7,
                improvement: 28.7,
                confidence: 96.5,
                isSignificant: true
              }
            },
            sampleSize: 5000,
            conversionRate: 28.7
          }
        ],
        insights: [
          {
            type: 'winner',
            title: 'Personalization Drives Engagement',
            description: 'Adding first name personalization increased open rates by 28.7%',
            confidence: 96.5,
            data: {}
          }
        ],
        recommendations: [
          'Apply personalization to similar templates',
          'Test urgency in CTA buttons',
          'Consider seasonal messaging optimization'
        ]
      }
    };
  };

  const generateDemoCampaign = async () => {
    setIsGenerating(true);
    setGenerationLog([]);

    try {
      const template = createDemoTemplate();
      const test = createDemoABTest(template);

      addToLog('🔍 Analyzing A/B test results...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Detect winner
      const winnerResult = await winnerDetectionEngine.detectWinner(test);
      addToLog(`✅ Winner detected: ${winnerResult.improvement.toFixed(1)}% improvement with ${winnerResult.confidence}% confidence`);
      await new Promise(resolve => setTimeout(resolve, 1000));

      addToLog('🤖 Generating AI-optimized campaign...');
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Generate campaign with proper WinnerDetectionResult structure
      const completeWinnerResult = {
        hasWinner: true,
        winnerVariantId: 'test-variant',
        confidence: 95.0,
        improvement: 15.2,
        recommendation: 'implement' as const,
        reasoning: 'Statistical significance achieved with high confidence',
        statisticalAnalysis: {
          improvement: 15.2,
          absoluteDifference: 3.2,
          relativeRisk: 1.15,
          oddsRatio: 1.18,
          statistical: {
            pValue: 0.01,
            zScore: 2.58,
            confidenceInterval: [8.5, 21.9] as [number, number],
            isSignificant: true,
            effectSize: 0.5,
            standardError: 0.02,
            powerAnalysis: 85,
            sampleSizeAdequacy: 'adequate' as const
          },
          businessImpact: {
            estimatedLift: 15.2,
            revenueImpact: 5000,
            confidenceLevel: 95
          }
        },
        businessImpact: {
          estimatedLift: 15.2,
          revenueImpact: 5000,
          riskAssessment: 'low' as const
        },
        nextActions: ['Deploy changes to production', 'Monitor campaign performance'],
        alerts: []
      };

      const campaign = await campaignService.generateCampaignFromWinner(test, completeWinnerResult, template);
      addToLog(`🎉 Campaign generated: "${campaign.name}"`);
      await new Promise(resolve => setTimeout(resolve, 500));

      addToLog(`📧 Generated ${campaign.subjectLines.length} optimized subject lines`);
      addToLog(`👥 Created ${campaign.audienceSegments.length} audience segments`);
      addToLog(`🎯 Applied ${campaign.personalizationRules.length} personalization rules`);
      await new Promise(resolve => setTimeout(resolve, 500));

      addToLog(`📊 Performance prediction: ${campaign.performancePrediction.openRate.predicted.toFixed(1)}% open rate`);
      addToLog('✨ Campaign ready for deployment!');

      // Update campaigns list
      const allCampaigns = campaignService.getGeneratedCampaigns();
      setCampaigns(allCampaigns);
      setSelectedCampaign(campaign);

    } catch (error) {
      addToLog(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateMultipleGenerations = async () => {
    setIsGenerating(true);
    setGenerationLog([]);

    try {
      let currentTemplate = createDemoTemplate();

      for (let generation = 1; generation <= 3; generation++) {
        addToLog(`🔄 Generating campaign - Generation ${generation}`);

        const test = createDemoABTest(currentTemplate);
        test.id = `demo-test-gen-${generation}-${Date.now()}`;
        test.name = `Summer Sale Test - Generation ${generation}`;

        // Simulate different improvements for each generation
        const improvements = [28.7, 15.2, 8.9];
        const confidences = [96.5, 92.1, 89.3];

        const winnerResult = {
          hasWinner: true,
          winnerVariantId: 'test-variant',
          confidence: confidences[generation - 1],
          improvement: improvements[generation - 1],
          recommendation: 'implement' as const,
          reasoning: 'Statistical significance achieved with high confidence',
          statisticalAnalysis: {
            improvement: improvements[generation - 1],
            absoluteDifference: improvements[generation - 1] * 0.2,
            relativeRisk: 1 + (improvements[generation - 1] / 100),
            oddsRatio: 1 + (improvements[generation - 1] / 100) * 1.1,
            statistical: {
              pValue: 0.01,
              zScore: 2.58,
              confidenceInterval: [improvements[generation - 1] * 0.7, improvements[generation - 1] * 1.3] as [number, number],
              isSignificant: true,
              effectSize: 0.5,
              standardError: 0.02,
              powerAnalysis: 85,
              sampleSizeAdequacy: 'adequate' as const
            },
            businessImpact: {
              estimatedLift: improvements[generation - 1],
              revenueImpact: improvements[generation - 1] * 200,
              confidenceLevel: confidences[generation - 1]
            }
          },
          businessImpact: {
            estimatedLift: improvements[generation - 1],
            revenueImpact: improvements[generation - 1] * 200,
            riskAssessment: 'low' as const
          },
          nextActions: ['Deploy changes to production', 'Monitor campaign performance'],
          alerts: []
        };

        addToLog(`📈 Generation ${generation}: ${winnerResult.improvement.toFixed(1)}% improvement`);

        const campaign = await campaignService.generateCampaignFromWinner(test, winnerResult, currentTemplate);
        addToLog(`✅ Generated: "${campaign.name}"`);

        // Use evolved template for next generation
        currentTemplate = campaign.template;

        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      addToLog('🎯 Multi-generational evolution complete!');

      // Update campaigns list
      const allCampaigns = campaignService.getGeneratedCampaigns();
      setCampaigns(allCampaigns);

      // Show evolution data
      const evolution = campaignService.getTemplateEvolution('demo-template-1');
      if (evolution) {
        addToLog(`📊 Evolution tracked: ${evolution.generations.length} generations`);
        addToLog(`🧠 Learnings captured: ${evolution.learnings.length} insights`);
      }

    } catch (error) {
      addToLog(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const addToLog = (message: string) => {
    setGenerationLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const clearCampaigns = () => {
    setCampaigns([]);
    setSelectedCampaign(null);
    setGenerationLog([]);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-content mb-4">
          AI-Driven Campaign Generation Demo
        </h1>
        <p className="text-lg text-content-secondary mx-auto">
          Experience how AI automatically generates optimized campaigns from A/B test winners,
          with multi-generational template evolution and performance-based subject line optimization.
        </p>
      </div>

      {/* Demo Controls */}
      <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
        <h2 className="text-xl font-semibold text-content mb-4">Demo Controls</h2>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={generateDemoCampaign}
            disabled={isGenerating}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Generating...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate Single Campaign
              </>
            )}
          </button>

          <button
            onClick={generateMultipleGenerations}
            disabled={isGenerating}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Multi-Generation Evolution
          </button>

          <button
            onClick={clearCampaigns}
            disabled={isGenerating}
            className="inline-flex items-center px-4 py-2 border border-line-strong text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-surface hover:bg-gray-50 dark:hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Clear All
          </button>
        </div>
      </div>

      {/* Generation Log */}
      {generationLog.length > 0 && (
        <div className="bg-gray-900 rounded-lg p-4">
          <h3 className="text-lg font-medium text-white mb-3">Generation Log</h3>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {generationLog.map((log, index) => (
              <div key={index} className="text-sm text-gray-300 font-mono">
                {log}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campaign Dashboard */}
      {campaigns.length > 0 && (
        <CampaignGenerationDashboard
          onCampaignSelect={setSelectedCampaign}
          onTemplateEvolutionView={(evolution) => {
            console.log('Template Evolution:', evolution);
          }}
        />
      )}

      {/* Feature Highlights */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8">
        <h2 className="text-2xl font-semibold text-content mb-6">Key Features Demonstrated</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-surface rounded-lg p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-medium text-content">Winner-Based Generation</h3>
            </div>
            <p className="text-content-secondary">
              Automatically generates optimized campaigns from A/B test winners with high confidence thresholds.
            </p>
          </div>

          <div className="bg-surface rounded-lg p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600 dark:text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-medium text-content">Multi-Generational Evolution</h3>
            </div>
            <p className="text-content-secondary">
              Tracks template evolution across generations with performance improvements and learning extraction.
            </p>
          </div>

          <div className="bg-surface rounded-lg p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-purple-600 dark:text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-1.586l-4 4z" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-medium text-content">Subject Line Optimization</h3>
            </div>
            <p className="text-content-secondary">
              Builds performance-based subject line repository with emotional trigger analysis and personalization.
            </p>
          </div>

          <div className="bg-surface rounded-lg p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-medium text-content">Audience Segmentation</h3>
            </div>
            <p className="text-content-secondary">
              Creates intelligent audience segments with performance predictions and optimization recommendations.
            </p>
          </div>

          <div className="bg-surface rounded-lg p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600 dark:text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-medium text-content">Performance Prediction</h3>
            </div>
            <p className="text-content-secondary">
              Predicts campaign performance with confidence intervals, risk factors, and optimization opportunities.
            </p>
          </div>

          <div className="bg-surface rounded-lg p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-medium text-content">Personalization Rules</h3>
            </div>
            <p className="text-content-secondary">
              Generates dynamic personalization rules with content variations and expected performance lift.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignGenerationDemo;